import { getAuth } from 'firebase/auth';
import { randomSuggestion } from '../engine/suggestions';
import { paintStrokes } from '../engine/raster';

// The bot players.
//
// Everything here goes through the telestrations-ai Lambda rather than talking
// to Anthropic directly - the API key must never be in a browser bundle. The
// endpoint verifies a Firebase ID token, so only somebody signed in and
// approved can spend money on it, and this is the one place that attaches the
// token so no caller can forget.
//
// MODEL: Haiku, the cheapest there is. Matt: "let's go with the worst,
// cheapest model we can because them being bad at drawing is sort of the
// point." That's not a compromise - a bot that draws well and reads drawings
// perfectly makes a WORSE game, because Telestrations is the chain falling
// apart. Cheap and bad happen to be the same direction here.

const API = process.env.VUE_APP_AI_API_URL || '';

export const botsAvailable = () => Boolean(API);

/**
 * The render the bot is shown before guessing - small, and that is the
 * handicap.
 *
 * Matt's call: a vision model reading a full-resolution drawing is too good,
 * and a chain that stops degrading stops being funny. 128px is roughly what
 * you'd see glancing at somebody's phone across a table: enough to commit to
 * a confident wrong answer, not enough to study.
 */
export const GUESS_RENDER_PX = 128;

/**
 * Strokes -> base64 PNG. Canvas rather than SVG because the vision endpoint
 * wants a raster, and because downscaling here is what does the handicapping.
 */
export const renderStrokes = (strokes, size = GUESS_RENDER_PX) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  paintStrokes(canvas.getContext('2d'), strokes, size);
  return canvas.toDataURL('image/png').split(',')[1];
};

async function call(route, payload) {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Bots need a signed-in user.');
  const idToken = await user.getIdToken();
  const res = await fetch(`${API}/${route}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Bot ${route} failed: HTTP ${res.status}`);
  return res.json();
}

/**
 * Every one of these falls back rather than throwing.
 *
 * A bot that can't reach the API must not wedge the round - it fumbles its
 * turn and the game carries on. A blank page from a bot reads, in the reveal,
 * exactly like a person who got distracted, which is a perfectly good outcome.
 */
export const botPhrase = async () => {
  try {
    const { phrase } = await call('phrase', {});
    return phrase || randomSuggestion();
  } catch {
    return randomSuggestion();
  }
};

export const botDraw = async (phrase) => {
  try {
    const { strokes } = await call('draw', { phrase });
    return Array.isArray(strokes) && strokes.length ? strokes : [];
  } catch {
    return [];
  }
};

export const botGuess = async (strokes) => {
  try {
    if (!strokes?.length) return 'a blank page';
    const { guess } = await call('guess', { image: renderStrokes(strokes), mediaType: 'image/png' });
    return guess || 'no idea';
  } catch {
    return 'no idea';
  }
};

/** What this bot owes the table this step. */
export const botPageFor = async (kind, prompt) => {
  if (kind === 'word') return botPhrase();
  if (kind === 'draw') return botDraw(typeof prompt?.content === 'string' ? prompt.content : '');
  return botGuess(Array.isArray(prompt?.content) ? prompt.content : []);
};

// Bots are named after the fact that they are bots. Nobody should be fooled
// into thinking a real person drew a barcode.
export const BOT_NAMES = ['Robot 1', 'Robot 2', 'Robot 3', 'Robot 4', 'Robot 5', 'Robot 6'];
